import type React from 'react';

import * as api from '@gitbook/api-client';
import { Modal } from '@gitbook/react-uikit';
import { Alert, Button, Link } from '@gitbook/spine-react';

import { type GitSyncCurrentStatus, GitSyncProviderName } from '../../../Components';
import { DocumentationLink } from '../../../Components/Link';
import { useContactSupport } from '../../../state';
import { createDocumentationURL } from '../../../utils';

/**
 * Modal to display when a Git synchronization on a space has failed.
 */
export function SpaceGitSyncErrorModal(props: {
    space: api.Space;
    gitSyncStatus: GitSyncCurrentStatus | null;
    onClose: () => void;
}): React.ReactElement {
    const { space, gitSyncStatus, onClose } = props;

    const contactSupport = useContactSupport();

    const provider = GitSyncProviderName[space.gitSync?.installationProvider ?? 'github'];

    return (
        <Modal
            size="large"
            title="Git Sync failure"
            onClose={onClose}
            footer={
                <div className="flex justify-end gap-2">
                    <Button label="Close" kind="secondary" onPress={onClose} />
                    <Button
                        label="Troubleshooting guide"
                        kind="primary"
                        href={createDocumentationURL('product-tour/git-sync/troubleshooting')}
                    />
                </div>
            }
        >
            <div className="flex flex-col">
                <div className="mt-2">
                    {gitSyncStatus?.direction === api.GitSyncOperationDirection.Import
                        ? `An error was detected while importing recent commits from ${provider}, to resume synchronization & merging of change requests please fix the error.`
                        : `An error was detected while exporting the latest changes to ${provider}. It is recommended to fix the error before merging further change requests.`}
                </div>
                <Alert
                    kind="danger"
                    details={gitSyncStatus?.error ?? 'Unknown error'}
                    className="mt-4"
                />
                <div className="mt-4">
                    Refer to our{' '}
                    <DocumentationLink path="product-tour/git-sync/troubleshooting">
                        troubleshooting guide
                    </DocumentationLink>{' '}
                    for more information or{' '}
                    <Link onPress={() => contactSupport()}>contact support</Link> if the problem
                    persists.
                </div>
            </div>
        </Modal>
    );
}
